import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './CartItems.css';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { useAddresses } from '../../hooks/useAddresses';
import { useShipping } from '../../hooks/useShipping';
import { LoadingSpinner, EmptyState } from '../UI';
import { getProductImageUrl, getProductId, getProductName, formatPrice } from '../../utils/helpers';

const CartItems = () => {
  const { t } = useTranslation('cart');
  const { 
    items, 
    totalPrice, 
    totalItems,
    isLoading, 
    removeItem, 
    increaseQuantity, 
    decreaseQuantity,
    isUpdating,
    isRemoving
  } = useCart();
  
  const { addresses } = useAddresses();
  const { shippingFee, calculateShipping, isCalculating } = useShipping();
  const calculateShippingRef = useRef(calculateShipping);
  const lastCalculatedProvinceRef = useRef(null);

  // Keep ref updated
  useEffect(() => {
    calculateShippingRef.current = calculateShipping;
  }, [calculateShipping]);

  // Calculate shipping fee based on default address (like st_cael mobile app)
  useEffect(() => {
    if (!addresses || addresses.length === 0) {
      // No address, use default fee (like st_cael mobile app)
      calculateShippingRef.current(null);
      return;
    }

    // Get default address or first address
    const defaultAddress = addresses.find(a => a.isDefault) || addresses[0];
    const neighborhood =
      defaultAddress?.neighborhood || defaultAddress?.state;

    if (neighborhood && neighborhood !== lastCalculatedProvinceRef.current) {
      lastCalculatedProvinceRef.current = neighborhood;
      calculateShippingRef.current(neighborhood);
    } else if (!neighborhood) {
      calculateShippingRef.current(null);
    }
  }, [addresses]);

  if (isLoading) {
    return (
      <section className="cart">
        <div className="cart-container">
          <LoadingSpinner text={t('loading')} />
        </div>
      </section>
    );
  }

  if (!items || items.length === 0) {
    return (
      <section className="cart">
        <div className="cart-container">
          <EmptyState
            icon="cart"
            title={t('empty')}
            description={t('emptyDescription')}
            actionText={t('continueShopping')}
            actionLink="/"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="cart">
      <div className="cart-container">
        <div className="cart-left">
          <h1 className="cart-title">{t('myCart')} ({totalItems} {totalItems > 1 ? t('items') : t('item')})</h1>
          
          {items.map((item) => {
            const product = item.product || item;
            // Use cart item ID (not product ID) for cart operations
            const itemId = item._id || item.id;
            // IMPORTANT: Use item.price first (contains bid price if it's a bid), fallback to product.price
            const price = item.price || product.price || 0;
            const name = getProductName(product);
            
              return (
              <div key={itemId} className="cart-item">
                  <div className="cart-item-img">
                  <img src={getProductImageUrl(product)} alt={name} />
                  </div>
                  <div className="cart-item-details">
                  <Link to={`/product/${getProductId(product)}`} className="cart-item-name">
                    <h2>{name}</h2>
                  </Link>
                  <p className="cart-item-price">{formatPrice(price)}</p>
                  
                  {/* Display SKU if available */}
                  {item.sku && (
                    <p className="cart-item-sku">
                      <span className="cart-item-sku-label">{t('sku')}:</span>
                      <span className="cart-item-sku-value">{item.sku}</span>
                    </p>
                  )}
                  
                  {(item.size || item.color) && (
                    <p className="cart-item-variant">
                      {item.size && <span>{t('size')}: {item.size}</span>}
                      {item.size && item.color && ' | '}
                      {item.color && <span>{t('color')}: {item.color}</span>}
                    </p>
                  )}
                  
                    <div className="cart-item-actions">
                    <div className="quantity-controls">
                      <button 
                        className="qty-btn"
                        onClick={() => decreaseQuantity(itemId)}
                        disabled={item.quantity <= 1 || isUpdating}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="qty-value">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() => increaseQuantity(itemId)}
                        disabled={isUpdating}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    
                    <button
                      onClick={() => removeItem(itemId)}
                      className="cart-item-remove"
                      disabled={isRemoving}
                    >
                      <Trash2 size={18} />
                      <span>{t('remove')}</span>
                    </button>
                  </div>
                  
                  <p className="cart-item-subtotal">
                    {t('subtotalLabel')}: {formatPrice(price * item.quantity)}
                  </p>
                  </div>
                </div>
              );
          })}
        </div>

        <div className="cart-right">
          <div className="cart-summary">
            <h2>{t('orderSummary')}</h2>
            <div className="cart-summary-row">
              <span>{t('subtotal')} ({totalItems} {totalItems > 1 ? t('items') : t('item')})</span>
              <span>{formatPrice(totalPrice)}</span>
            </div>
            <div className="cart-summary-row">
              <span>{t('shipping')}</span>
              <span>
                {isCalculating ? (
                  t('calculating')
                ) : shippingFee > 0 ? (
                  formatPrice(shippingFee)
                ) : (
                  t('calculatedAtCheckout')
                )}
              </span>
            </div>
            <div className="cart-summary-row">
              <span>{t('tax', { ns: 'checkout' })}</span>
              <span>{t('calculatedAtCheckout')}</span>
            </div>
            <div className="cart-summary-row total">
              <strong>{t('total')}</strong>
              <strong>
                {formatPrice(totalPrice + (shippingFee || 0))}
              </strong>
            </div>
            <Link to="/checkout" className="checkout-btn-link">
              <button className="checkout-btn">{t('proceedToCheckout')}</button>
            </Link>
            <Link to="/" className="continue-shopping-link">
              {t('continueShopping')}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CartItems;
